import React, { useEffect, useState, useRef } from "react";
import { X, MoreVertical, Plus, Edit, Trash2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import DeliveryPaymentFlow from "../Materials/Delivery/DelivaryPaymentFlow";
import {
    NewPaymentModal,
    NewCommentModal,
    deleteComment
} from "../Materials/Delivery/DeliveryModals";

const MaterialPurchaseDetails = ({ Delivery, onClose, setTab, setDelivery, userPermissions }) => {
    const [comments, setComments] = useState([]);
    const [showPaymentFlow, setShowPaymentFlow] = useState(false);
    const [showNewPayment, setShowNewPayment] = useState(false);
    const [showNewComment, setShowNewComment] = useState(false);
    const [showNewCustomField, setShowNewCustomField] = useState(false);
    const [showEditCustomField, setShowEditCustomField] = useState(false);
    const [selectedCustomField, setSelectedCustomField] = useState(null);
    const [partPayments, setPartPayments] = useState([]);
    const [delivery, setDeliveryState] = useState(Delivery);
    const [customFields, setCustomFields] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const menuRef = useRef(null);

    console.log("Delivery Details", delivery);

    const formatDate = (date) => {
        const d = new Date(date);

        // Format time as "hh:mmam/pm"
        let time = d
            .toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
            .toLowerCase();

        // Remove the space between time and am/pm
        time = time.replace(" ", "");

        // Format date as "dd Month, yyyy"
        const day = d.getDate().toString().padStart(2, "0");
        const month = d.toLocaleString("default", { month: "long" });
        const year = d.getFullYear();

        return `${time}, ${day} ${month}, ${year}`;
    };

    const getPartPayments = async () => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/list-partpayment-of-delivery/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ delivery: delivery?.id }),
                }
            );
            const data = await response.json();
            if (data.status) {
                setPartPayments(data.data);
            }
        } catch (error) {
            console.error("Error fetching part payments:", error);
        }
    };

    const handleReceiveClick = () => {
        setShowPaymentFlow(true);
    };

    const handleSaveClick = () => {
        setIsEditMode(false);
        onClose();                               // close the drawer
    };

    const handleDeleteDelivery = async () => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/delete-delivery/${delivery?.id}/`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const data = await response.json();
            if (data.status) {
                // Close the modal and notify parent component if needed
                onClose();
                // You might want to call a callback here to refresh the parent list
            } else {
                alert(data.message || "Failed to delete delivery");
            }
        } catch (error) {
            console.error("Error deleting delivery:", error);
            alert("An error occurred while deleting the delivery");
        }
    };
    const handleDeleteComment = async (commentId) => {
        const success = await deleteComment(commentId);
        if (success) {
            fetchComments(); // Refresh comments after deletion
        }
    };

    const handlePaymentFlowClose = () => {
        setShowPaymentFlow(false);
        onClose();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [menuOpen]);

    const getDelivery = async () => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/get-a-delivery/${Delivery?.id}/`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const data = await response.json();
            if (data?.status) {
                setDeliveryState(data?.data);
                if (data?.data?.custom_fields) {
                    setCustomFields(data?.data?.custom_fields);
                }
            }
        } catch (error) {
            console.error("Error fetching delivery:", error);
        }
    };

    const fetchComments = async () => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/get-all-comments-of-delivery/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ delivery: delivery?.id }),
                }
            );
            const data = await response.json();
            if (data.status) {
                setComments(data.data);
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
        }
    };

    const handleAddCustomField = async (name, amount) => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(`${BASE_URL}/inventory/add-custom-price/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    transaction_id: delivery?.id,
                    name: name,
                    amount: amount,
                }),
            });
            const data = await response.json();
            if (data.status) {
                getDelivery(); // Refresh delivery data to get updated custom fields
                setShowNewCustomField(false);
            } else {
                alert(data.message || "Failed to add custom field");
            }
        } catch (error) {
            console.error("Error adding custom field:", error);
            alert("An error occurred while adding custom field");
        }
    };

    const handleEditCustomField = async (name, amount) => {
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(`${BASE_URL}/inventory/edit-custom-price/`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    transaction_id: delivery?.id,
                    field_name: name,
                    value: amount,
                }),
            });
            const data = await response.json();
            if (data.status) {
                getDelivery(); // Refresh delivery data to get updated custom fields
                setShowEditCustomField(false);
                setSelectedCustomField(null);
            } else {
                alert(data.message || "Failed to update custom field");
            }
        } catch (error) {
            console.error("Error updating custom field:", error);
            alert("An error occurred while updating custom field");
        }
    };

    const handleDeleteCustomField = async (name) => {
        try {
            if (
                !window.confirm(`Are you sure you want to delete the ${name} field?`)
            ) {
                return;
            }

            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/inventory/delete-custom-price/`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        transaction_id: delivery?.id,
                        field_name: name,
                    }),
                }
            );
            const data = await response.json();
            if (data.status) {
                getDelivery(); // Refresh delivery data to get updated custom fields
            } else {
                alert(data.message || "Failed to delete custom field");
            }
        } catch (error) {
            console.error("Error deleting custom field:", error);
            alert("An error occurred while deleting custom field");
        }
    };

    useEffect(() => {
        if (Delivery?.id) {
            getPartPayments();
            fetchComments();
            getDelivery();
        }
    }, [Delivery?.id]);

    return (
        <div className="w-full bg-gray-50 p-4 relative">
            {showPaymentFlow ? (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="max-w-4xl w-full mx-4">
                        <DeliveryPaymentFlow
                            onClose={handlePaymentFlowClose}
                            delivery={delivery}
                        />
                    </div>
                </div>
            ) : (
                <div className="">
                    <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-lg shadow-sm">
                        <button className="text-gray-600" onClick={onClose}>
                            <X size={20} />
                        </button>
                        <h2 className="text-lg font-medium">Material Purchase Details</h2>
                        <div className="relative" ref={menuRef}>
                            <button
                                className="text-gray-600"
                                onClick={() => setMenuOpen(!menuOpen)}
                            >
                                <MoreVertical size={20} />
                            </button>

                            {menuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-md z-20 border border-gray-200 overflow-hidden transition-all duration-200 ease-in-out">
                                    <button
                                        className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors duration-150"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            setShowDeleteConfirm(true);
                                        }}
                                        aria-label="Delete Delivery"
                                    >
                                        <Trash2 size={18} className="text-red-500" />
                                        <span>Delete Delivery</span>
                                    </button>
                                    <button
                                        className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors duration-150 border-t border-gray-100"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            setIsEditMode(true);
                                        }}
                                        aria-label="Edit Delivery"
                                    >
                                        <Edit size={18} className="text-gray-600" />
                                        <span>Edit</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className="flex flex-col items-center gap-4 h-[88%]"
                        style={{
                            overflowY: "auto",
                        }}
                    >
                        <div className="flex flex-row justify-center gap-3 w-full">
                            <div className="flex flex-col w-[48%] items-center gap-4">
                                {/* Delivery Details */}
                                <div className="bg-white p-6 rounded-lg w-full shadow-sm space-y-4">
                                    <div className="flex justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">Delivery Number</p>
                                            <p className="font-medium">{delivery?.po_number ? `#${delivery?.po_number}` : `#DLRV-${delivery?.id}`}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-gray-500">
                                                {delivery?.transaction_id === 1
                                                    ? `Expected on`
                                                    : `Received on`}
                                            </p>
                                            <p className="font-medium">
                                                {delivery?.transaction_id === 1
                                                    ? `${delivery?.date}`
                                                    : `${new Date(
                                                        delivery?.last_updated_on
                                                    ).toLocaleDateString()}`}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">
                                            {delivery?.vendor_name}{" "}
                                            <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                                                Shop
                                            </span>
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600">
                                                {delivery?.contact_no}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Materials */}
                                <div className="bg-white p-6 w-full rounded-lg shadow-sm">
                                    <div className="flex items-center gap-2 mb-4">
                                        <h3 className="font-medium">Materials</h3>
                                        <span className="bg-gray-100 text-sm px-2 py-0.5 rounded">
                                            {delivery?.all_materials?.length}
                                        </span>
                                    </div>

                                    {delivery?.all_materials?.map((material, index) => (
                                        <div key={material.id} className="mb-4 last:mb-0">
                                            <div className="flex justify-between mb-2">
                                                <div className="flex gap-2 items-center">
                                                    <span className="text-gray-500 text-sm">
                                                        {String(index + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-medium">
                                                        {material.material_name}
                                                    </span>
                                                    <span className="text-gray-500 text-sm">
                                                        Subcategory- {material.material_subcategory}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Quantity</p>
                                                    <p className="font-medium">
                                                        {material.quantity} {material.unit}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Price/Unit</p>
                                                    <p className="font-medium">₹ {material.unit_price}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Total Price</p>
                                                    <p className="font-medium">
                                                        ₹ {material.total_price}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Custom Fields Section */}
                                <div className="bg-white p-6 w-full rounded-lg shadow-sm">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="font-medium">Custom Price Fields</h3>
                                        {isEditMode && (
                                            <button
                                                onClick={() => setShowNewCustomField(true)}
                                                className="flex items-center gap-1 text-sm bg-[#ECE6F0] px-3 py-1.5 rounded-full"
                                            >
                                                New <Plus size={16} />
                                            </button>
                                        )}
                                    </div>

                                    {Object.keys(customFields).length === 0 ? (
                                        <p className="text-gray-500">No custom fields added</p>
                                    ) : (
                                        <div className="space-y-3">
                                            {Object.entries(customFields).map(([name, amount]) => (
                                                <div
                                                    key={name}
                                                    className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                                                >
                                                    <div>
                                                        <p className="font-medium">{name}</p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <p className="font-medium">₹ {amount}</p>
                                                        {isEditMode && (
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedCustomField({ name, amount });
                                                                        setShowEditCustomField(true);
                                                                    }}
                                                                    className="text-gray-500 hover:text-blue-600"
                                                                >
                                                                    <Edit size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteCustomField(name)}
                                                                    className="text-gray-500 hover:text-red-600"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex justify-between mt-4 pt-4 border-t">
                                        <span className="font-medium">Grand Total</span>
                                        <span className="font-medium">
                                            ₹ {delivery?.total_price}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col w-[48%] items-center gap-4">
                                {/* Payment Entries */}
                                <div className="bg-white p-6 w-full rounded-lg shadow-sm">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="font-medium">Payment Entries</h3>
                                        {isEditMode && Number(delivery?.remaining_payment) !== 0 && (
                                            <button
                                                onClick={() => setShowNewPayment(true)}
                                                className="flex items-center gap-1 text-sm bg-[#ECE6F0] px-3 py-1.5 rounded-full"
                                            >
                                                New <Plus size={16} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        {partPayments?.map((payment) => (
                                            <div
                                                key={payment.id}
                                                className="flex justify-between items-start"
                                            >
                                                <div>
                                                    <p className="text-sm">
                                                        {new Date(payment.created_on).toLocaleString()}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {payment.payment_mathod} • {payment.remarks || "No remarks"}
                                                    </p>
                                                </div>
                                                <span className="font-medium">₹ {payment.amount}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 pt-4 border-t space-y-2">
                                        <div className="flex justify-between">
                                            <span className="font-medium">Total Paid Amount</span>
                                            <span className="font-medium text-green-600">
                                                ₹ {delivery?.paid_amount}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium">Remaining Amount</span>
                                            <span className="font-medium text-red-500">
                                                ₹ {Number(delivery?.remaining_payment ?? 0).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6 w-full">
                                    {/* Comments Section */}
                                    <div className="bg-white p-6 rounded-lg shadow-sm">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="font-medium">Comments/Remarks</h3>
                                            {isEditMode && (
                                                <button
                                                    onClick={() => setShowNewComment(true)}
                                                    className="flex items-center gap-1 text-sm bg-[#ECE6F0] px-3 py-1.5 rounded-full"
                                                >
                                                    New <Plus size={16} />
                                                </button>
                                            )}
                                        </div>
                                        {comments.length === 0 && <p>No comments available</p>}
                                        <div className="space-y-3">
                                            {comments.map((comment) => (
                                                <div
                                                    key={comment.id}
                                                    className="flex justify-between items-start"
                                                >
                                                    <div>
                                                        <p className="text-sm text-gray-500">
                                                            {formatDate(comment.created_on)}
                                                        </p>
                                                        <p className="text-base text-black font-medium">
                                                            {comment.comment}
                                                        </p>
                                                    </div>
                                                    <button
                                                        className={`${isEditMode ? "text-gray-400 hover:text-gray-600" : "text-gray-300 cursor-not-allowed"}`}
                                                        disabled={!isEditMode}
                                                        aria-disabled={!isEditMode}
                                                        onClick={() => {
                                                            if (!isEditMode) return;
                                                            handleDeleteComment(comment.id);
                                                        }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Payment Slips Section */}
                                    <div className="bg-white p-6 rounded-lg shadow-sm">
                                        <div className="flex justify-between w-full items-center mb-4">
                                            <h3 className="font-medium">Payment Slips</h3>
                                        </div>
                                        {delivery?.receipt_image ? (
                                            <div className="grid grid-cols-3 w-full gap-4">
                                                <div className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center">
                                                    <img
                                                        src={delivery?.receipt_image}
                                                        alt="Receipt"
                                                        className="object-fill h-full w-full rounded-lg"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <p>No payment slips available</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {delivery?.transaction_id === 1 && (
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={handleSaveClick}
                                    className={`px-6 py-2.5 ${isEditMode
                                        ? "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        } rounded-full flex items-center gap-2`}
                                    disabled={!isEditMode}
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>

                    {/* New Payment Modal */}
                    {showNewPayment && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <NewPaymentModal
                                onClose={() => setShowNewPayment(false)}
                                delivery={delivery}
                                onSuccess={() => {
                                    getPartPayments();
                                    getDelivery();
                                }}
                            />
                        </div>
                    )}

                    {/* New Comment Modal */}
                    {showNewComment && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <NewCommentModal
                                onClose={() => setShowNewComment(false)}
                                delivery={delivery}
                                onSuccess={() => {
                                    const refresh = async () => {
                                        await fetchComments();
                                        await getDelivery();
                                    };
                                    refresh();
                                }}
                            />
                        </div>
                    )}

                    {/* New Custom Field Modal */}
                    {showNewCustomField && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <NewCustomFieldModal
                                onClose={() => setShowNewCustomField(false)}
                                onSubmit={handleAddCustomField}
                            />
                        </div>
                    )}

                    {/* Edit Custom Field Modal */}
                    {showEditCustomField && selectedCustomField && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <EditCustomFieldModal
                                onClose={() => {
                                    setShowEditCustomField(false);
                                    setSelectedCustomField(null);
                                }}
                                fieldName={selectedCustomField.name}
                                fieldValue={selectedCustomField.amount}
                                onSubmit={handleEditCustomField}
                            />
                        </div>
                    )}
                    {showDeleteConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                                <h3 className="text-lg font-medium mb-4">Confirm Delete</h3>
                                <p className="mb-6">
                                    Are you sure you want to delete this delivery and associated
                                    inventory updates? This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                        onClick={() => setShowDeleteConfirm(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
                                        onClick={handleDeleteDelivery}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// New Custom Field Modal Component
const NewCustomFieldModal = ({ onClose, onSubmit }) => {
    const [name, setName] = useState("");
    const [amount, setAmount] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim() || !amount.trim()) {
            alert("Please fill in all fields");
            return;
        }
        onSubmit(name, amount);
    };

    return (
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Add Custom Field</h3>
                <button onClick={onClose} className="text-gray-500">
                    <X size={20} />
                </button>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Field Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                            placeholder="e.g. Tax, Discount, Shipping"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount (₹)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                            placeholder="0.00"
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-white bg-[#3B0069] rounded-md hover:bg-purple-800"
                        >
                            Add Field
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

// Edit Custom Field Modal Component
const EditCustomFieldModal = ({ onClose, fieldName, fieldValue, onSubmit }) => {
    const [amount, setAmount] = useState(fieldValue);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!amount.toString().trim()) {
            alert("Please enter an amount");
            return;
        }
        onSubmit(fieldName, amount);
    };

    return (
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Edit {fieldName}</h3>
                <button onClick={onClose} className="text-gray-500">
                    <X size={20} />
                </button>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount (₹)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-white bg-[#3B0069] rounded-md hover:bg-purple-800"
                        >
                            Update
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MaterialPurchaseDetails;